import { useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Switch from "@mui/material/Switch";
import Divider from "@mui/material/Divider";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import ActionDialog from "components/admin/ActionDialog";
import GiftEditor from "components/admin/GiftEditor";
import { useRunner } from "components/admin/hooks";
import useConfirmChanges from "components/admin/confirmChanges";
import api from "api/client";

export const KIND_NAMES = { 0: "Thưởng ngày", 1: "Thưởng mùa" };

/** Thêm / sửa một mốc thưởng hạng (khoảng hạng + quà). */
function RewardTierDialog({ tier, kind, nextFrom, onClose, onDone }) {
  const [run, busy] = useRunner();
  const ask = useConfirmChanges();
  const [form, setForm] = useState(() =>
    tier
      ? { rankFrom: tier.rankFrom, rankTo: tier.rankTo, gift: tier.gift, enabled: tier.enabled }
      : { rankFrom: nextFrom, rankTo: nextFrom, gift: [[17, 10]], enabled: true }
  );
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    const name = KIND_NAMES[tier ? tier.kind : kind];
    const to = Number(form.rankTo);
    const rankLabel = (from, upTo) =>
      Number(upTo) === 0 ? `hạng ${from} trở xuống` : `hạng ${from} - ${upTo}`;
    const yes = await ask({
      title: tier ? `Lưu mốc ${name.toLowerCase()}` : `Thêm mốc ${name.toLowerCase()}`,
      isNew: !tier,
      before: tier,
      after: form,
      fields: [
        ["rankFrom", "Từ hạng"],
        ["rankTo", "Đến hạng (0 = trở xuống)"],
        ["gift", "Quà"],
        ["enabled", "Đang áp dụng"],
      ],
      notes: [
        !tier &&
          `Người ${rankLabel(form.rankFrom, to)} nhận quà ở mỗi lần chốt ${name.toLowerCase()}.`,
        "Áp dụng từ lần chốt tiếp theo; quà đã gửi không đổi.",
      ],
    });
    if (!yes) return;
    const body = {
      kind: tier ? tier.kind : kind,
      rankFrom: Number(form.rankFrom),
      rankTo: Number(form.rankTo),
      gift: form.gift,
      enabled: Boolean(form.enabled),
    };
    const { ok } = await run(
      () =>
        tier
          ? api.put(`/api/admin/mythic-arena/rewards/${tier.id}`, body)
          : api.post("/api/admin/mythic-arena/rewards", body),
      (text) => text
    );
    if (ok) {
      onClose();
      onDone();
    }
  };

  const kindName = KIND_NAMES[tier ? tier.kind : kind];
  return (
    <ActionDialog
      open
      maxWidth="md"
      title={tier ? `Sửa mốc ${kindName.toLowerCase()}` : `Thêm mốc ${kindName.toLowerCase()}`}
      description="Hạng là hạng hiển thị trong game lúc chốt (boss bảng hạng cũng chiếm chỗ). Quà đã chốt được lưu riêng, sửa mốc chỉ áp dụng cho lần chốt sau. Thưởng ngày không được có vàng (chỉ thưởng mùa có vàng)."
      color="warning"
      submitText="Lưu"
      busy={busy}
      onClose={onClose}
      onSubmit={submit}
    >
      <Grid container spacing={2}>
        <Grid item xs={6} md={4}>
          <MDInput
            fullWidth
            type="number"
            label="Từ hạng"
            value={form.rankFrom}
            inputProps={{ min: 1 }}
            onChange={(e) => set({ rankFrom: e.target.value })}
          />
        </Grid>
        <Grid item xs={6} md={4}>
          <MDInput
            fullWidth
            type="number"
            label="Đến hạng (0 = trở xuống hết)"
            value={form.rankTo}
            inputProps={{ min: 0 }}
            onChange={(e) => set({ rankTo: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MDBox display="flex" alignItems="center" height="100%">
            <Switch
              checked={Boolean(form.enabled)}
              onChange={(e) => set({ enabled: e.target.checked })}
            />
            <MDTypography variant="caption">Đang áp dụng</MDTypography>
          </MDBox>
        </Grid>
      </Grid>
      <Divider sx={{ my: 2 }} />
      <GiftEditor
        value={form.gift}
        onChange={(gift) => set({ gift })}
        label="Phần thưởng gửi vào thư"
        randomNote="Thưởng hạng nên dùng quà cố định để người chơi biết trước mình nhận gì."
      />
    </ActionDialog>
  );
}

RewardTierDialog.defaultProps = { tier: null, kind: 0, nextFrom: 1 };

RewardTierDialog.propTypes = {
  tier: PropTypes.shape({
    id: PropTypes.number,
    kind: PropTypes.number,
    rankFrom: PropTypes.number,
    rankTo: PropTypes.number,
    gift: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
    enabled: PropTypes.bool,
  }),
  kind: PropTypes.number,
  nextFrom: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

export default RewardTierDialog;
